import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Domanda,
  PraticaSelf,
  Sezione,
  RisposteSezione,
  RisposteSezioneIdSezioneEnum,
  SurveyRisposteSubjectTypeEnum,
} from '../../../api/generated/dob';
import {
  HomeCard,
  SurveyState,
  UserState,
  getAmlAnswersForMifid,
  getMifidAnswers,
  getMifidTemplate,
  retrieveOnboardingSelfPracticeData,
  saveMifidV2Full,
  resetMifidSurvey,
  updateStatusQuestionnaires,
  resetSurvey,
} from '../../../store';
import { useAppDispatch, useAppSelector } from '../../redux';
import { FieldValues, useForm } from 'react-hook-form';
import { getDominio } from '../../../store/features/dob/utility/utility-thunks';
import dobSurveyResolver from '../../../utils/dobSurveyResolver';
import { useIntl } from 'react-intl';
import {
  commonErrorMessages,
  homeOnboardingSelfMifidSurveyMessages,
} from '../../../messages';
import { someIsMultipleAnswer } from '../../../utils';
import {
  getMainAnswerIds,
  getUniqueAnswerValues,
  mergeAnswers,
  parseMultipleAnswer,
  splitMainAndSubAnswers,
} from '../../../utils/mifid-sub-answers';
import appRoutes from '../../../routing/app-routes';
import { GlobalActions } from '../../../model';

export const useMifidSurveyLogic = (actions: GlobalActions) => {
  const { showToast, closeToast, navigate } = actions;
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();

  const { name, surname, token } = useAppSelector<UserState>(
    (state) => state.data.user
  );
  const praticaSelf = useAppSelector(
    (state) => state.features.home.practiceData as HomeCard<PraticaSelf>
  );

  const {
    surveyAnswers: answers,
    amlAnswers,
    surveyTemplate: template,
    saveResponseStatus,
    updateQuestionnairesStatus,
  } = useAppSelector((state) => state.features.mifidSurvey as SurveyState);

  const codiceCliente = praticaSelf?.value?.codiceCliente;
  const codicePratica = praticaSelf?.value?.codicePratica;

  useEffect(() => {
    if (token) {
      dispatch(retrieveOnboardingSelfPracticeData());
    }
  }, [token, dispatch]);

  useEffect(() => {
    if (token) {
      dispatch(getMifidTemplate());
    }
  }, [token, dispatch]);

  useEffect(() => {
    if (codiceCliente && codicePratica && token) {
      dispatch(
        getMifidAnswers({
          idCliente: codiceCliente,
          idPratica: codicePratica,
        })
      );
      dispatch(
        getAmlAnswersForMifid({
          idCliente: codiceCliente,
          idPratica: codicePratica,
        })
      );
    }
  }, [codiceCliente, codicePratica, dispatch, token]);

  useEffect(() => {
    const domains = [];
    template?.sezioni?.forEach((s) =>
      s?.listaDomande?.forEach((d) => {
        let dominio = d.tipoDominio;
        if (dominio) {
          if (dominio === 'DOM_SETTORE_OCCUPAZIONE_I')
            dominio = 'DOM_SETTORE_OCCUPAZIONE_MIFID';
          if (dominio === 'DOM_PROFESSIONE_ATTUALE')
            dominio = 'DOM_PROFESSIONE_ATTUALE_MIFID';
          if (dominio === 'DOM_PROFESSIONE') dominio = 'DOM_PROFESSIONE_MIFID';

          domains.push(dominio);
        }
      })
    );

    dispatch(
      getDominio({
        listaDomini: [...new Set(domains)].map((d) => {
          return { idDominio: d };
        }),
      })
    );
  }, [dispatch, template]);

  // default values setting
  const [defaultValues, setDefaultValues] = useState<FieldValues>({});
  const RADIOCHECK_ESG = useMemo(() => [266, 268, 286, 284, 288, 270], []);
  const RADIOCHECK_OBB = useMemo(() => [170, 223, 247, 284], []);
  useEffect(() => {
    const reduceValues = (values: FieldValues, d: Domanda): FieldValues => {
      const internalQuestions = d.strutturaInternaDomande?.domande;
      if (internalQuestions != null && internalQuestions.length !== 0) {
        return internalQuestions.reduce(reduceValues, values);
      }
      values[`survey-${d.idDomanda}`] = undefined;
      return values;
    };

    let defaultVal: FieldValues = {};

    template?.sezioni?.forEach((s) => {
      const initial = s?.listaDomande?.reduce(reduceValues, {}) || {};
      defaultVal = Object.assign({}, defaultVal, initial);
    });

    if (amlAnswers?.precompilato && amlAnswers.risposte) {
      amlAnswers.risposte.forEach(
        (r: { idDomanda: number; idRisposte: number[] }) => {
          defaultVal[`survey-${r.idDomanda}`] = r.idRisposte?.join(',');
        }
      );
    }

    if (!answers?.listaDomande || !answers.listaDomande?.length) {
      setDefaultValues(defaultVal);
      return;
    }

    const val = answers.listaDomande.reduce((values, d) => {
      values[`survey-${d.idDomanda}`] = d.listaRisposte
        ?.map((r) =>
          r.descRisposta !== '' ? r.descRisposta : r.idRisposta?.toString()
        )
        .join(',');

      const isObb = RADIOCHECK_OBB.includes((d?.idDomanda || 0) - 1);
      const isEsg = RADIOCHECK_ESG.includes((d?.idDomanda || 0) - 1);

      if ((isObb || isEsg) && d.listaRisposte !== undefined) {
        const id = isObb
          ? RADIOCHECK_OBB.find((number) => number === (d?.idDomanda || 0) - 1)
          : RADIOCHECK_ESG.find((number) => number === (d?.idDomanda || 0) - 1);
        // Risposta principale e sotto-risposte vengono separate per id e non per
        // posizione: il BE può restituire risposte duplicate (es. 330,331,330,331)
        const { main, sub } = splitMainAndSubAnswers(
          getUniqueAnswerValues(d.listaRisposte),
          getMainAnswerIds(template?.sezioni, id)
        );
        values[`survey-${d.idDomanda}`] = isObb
          ? main[0] ?? ''
          : [...main, ...sub].join(',');
        values[`survey-${id}`] = sub.join(',');
      }
      return values;
    }, defaultVal);
    setDefaultValues(val);
  }, [template, answers, amlAnswers, RADIOCHECK_OBB, RADIOCHECK_ESG]);
  // ------
  const form = useForm({
    resolver: dobSurveyResolver({
      template: template ?? {},
      default: {
        errorMessage: formatMessage(commonErrorMessages.fieldNotValid),
      },
    }),
    mode: 'onChange',
    reValidateMode: 'onChange',
    shouldFocusError: false,
    shouldUnregister: false,
    criteriaMode: 'all',
    defaultValues,
  });
  const {
    reset,
    watch,
    setValue,
    formState: { errors },
  } = form;

  useEffect(() => {
    if (defaultValues) {
      reset(defaultValues);
      form.trigger();
    }
  }, [defaultValues, reset, form]);

  const data = watch();

  useEffect(() => {
    if (data['survey-224'] === '238') {
      setValue('survey-223', '');
    }
  }, [data, setValue]);

  // ESG
  useEffect(() => {
    const survey266 = data['survey-266'] ?? '';
    const survey267 =
      data['survey-267']?.split(',')?.filter((e: string) => e !== '') ?? [];
    const survey268 = data['survey-268'] ?? '';
    const survey269 =
      data['survey-269']?.split(',')?.filter((e: string) => e !== '') ?? [];
    const survey270 = data['survey-270'] ?? '';
    const survey271 =
      data['survey-271']?.split(',')?.filter((e: string) => e !== '') ?? [];
    if (data['survey-267'])
      if (survey267.includes('276')) {
        if (
          survey267.length - 1 !==
          (survey266?.split(',') ?? [])?.filter((e: string) => e !== '').length
        ) {
          setValue('survey-267', `276,${survey266}`);
          form.trigger();
        }
      } else if (survey266 !== '') {
        setValue('survey-266', '');
        form.trigger();
      }
    if (data['survey-269'])
      if (survey269.includes('282')) {
        if (
          survey269.length - 1 !==
          (survey268?.split(',') ?? [])?.filter((e: string) => e !== '').length
        ) {
          setValue('survey-269', `282,${survey268}`);
          form.trigger();
        }
      } else if (survey268 !== '') {
        setValue('survey-268', '');
        form.trigger();
      }
    if (data['survey-271'])
      if (survey271.includes('288')) {
        if (
          survey271.length - 1 !==
          (survey270?.split(',')?.filter((e: string) => e !== '') ?? []).length
        ) {
          setValue('survey-271', `288,${survey270}`);
          form.trigger();
        }
      } else if (survey270 !== '') {
        setValue('survey-270', '');
        form.trigger();
      }
  }, [data, form, setValue]);

  const getSaveMifidRequest = useCallback(
    (values: FieldValues) => {
      if (template == null) {
        return null;
      }

      const risposteSezione: RisposteSezione[] = template?.sezioni
        ?.filter((s) => s.idSezione !== 'HEADER_PF')
        ?.map((s) => {
          const listaDomande = s?.listaDomande ?? [];
          const questionList: Array<Domanda> = [];

          Object.keys(values).forEach((key) => {
            let idDomanda = Number(key.split('-')[1]);
            if (RADIOCHECK_OBB.includes(idDomanda)) idDomanda += 1;
            const answerValue: string = values[key] || '';
            const found = listaDomande.find((d) => {
              if (d?.strutturaInternaDomande?.domande) {
                return d.strutturaInternaDomande.domande?.some(
                  (dd) => dd.idDomanda === idDomanda
                );
              }
              return d.idDomanda === idDomanda;
            });

            const isExist = found != null;

            if (!isExist) return;

            let isMultipleAnswer;
            let hasParent = false;
            if (found?.strutturaInternaDomande?.domande) {
              hasParent = true;
              isMultipleAnswer = found?.strutturaInternaDomande?.domande?.some(
                (d) => someIsMultipleAnswer(d, idDomanda)
              );
            } else {
              isMultipleAnswer = someIsMultipleAnswer(found, idDomanda);
            }
            let answer;
            if (isMultipleAnswer) {
              answer = parseMultipleAnswer(answerValue);
            } else {
              answer = [
                {
                  descRisposta: answerValue,
                  idRisposta: Number(answerValue),
                },
              ];
            }

            const answerFound = questionList.find(
              (d) => d.idDomanda === idDomanda
            );
            if (answerFound) {
              answerFound.listaRisposte = mergeAnswers(
                answerFound.listaRisposte,
                answer
              );
            } else {
              if (hasParent) {
                if (
                  !questionList?.some((q) => q.idDomanda === found.idDomanda)
                ) {
                  questionList.push({
                    idDomanda: found.idDomanda,
                    listaRisposte: [
                      {
                        idRisposta: '' as unknown as number,
                      },
                    ],
                  });
                }
              }
              questionList.push({
                idDomanda,
                listaRisposte: answer,
              });
            }
          });

          return {
            idSezione: s.idSezione as RisposteSezioneIdSezioneEnum,
            listaDomande: questionList.concat(
              s?.listaDomande
                ?.filter(
                  (d) =>
                    !questionList?.some((dd) => d.idDomanda === dd.idDomanda)
                )
                .map((d) => ({
                  idDomanda: d.idDomanda,
                  listaRisposte:
                    d?.listaRisposte?.map((r) => ({
                      [r.descRisposta == null ? 'idRisposta' : 'descRisposta']:
                        r.descRisposta ?? r.idRisposta,
                    })) ?? [],
                })) ?? []
            ),
          };
        });

      return {
        codiceCliente,
        idIncarico: codicePratica,
        subjectType: SurveyRisposteSubjectTypeEnum.PhysicalSubject,
        flagProfilatura: true,
        risposteSezione,
      };
    },
    [template, codiceCliente, codicePratica, RADIOCHECK_OBB]
  );

  const onSubmit = useCallback(() => {
    const payload = getSaveMifidRequest(data);
    if (payload == null) return;
    dispatch(saveMifidV2Full({ ...payload, idCliente: codiceCliente }));
  }, [data, dispatch, getSaveMifidRequest]);

  useEffect(() => {
    if (saveResponseStatus === 'failed') {
      showToast({
        close: closeToast,
        title: formatMessage(commonErrorMessages.errorTitlePopup),
        description: formatMessage(commonErrorMessages.errorDescriptionDefault),
        type: 'error',
      });
    } else if (saveResponseStatus === 'succeeded') {
      dispatch(
        updateStatusQuestionnaires({
          amlAndFactaCrsState: 'Completed',
          mifidState: 'Completed',
        })
      );
    }
  }, [closeToast, dispatch, formatMessage, saveResponseStatus, showToast]);

  useEffect(() => {
    if (updateQuestionnairesStatus === 'succeeded') {
      showToast({
        close: closeToast,
        title: formatMessage(
          homeOnboardingSelfMifidSurveyMessages.finishedMifidTitle
        ),
        description: formatMessage(
          homeOnboardingSelfMifidSurveyMessages.finishedMifidDesc,
          {
            customerName: name + ' ' + surname,
          }
        ),
        type: 'success',
      });
      dispatch(resetSurvey());
      dispatch(resetMifidSurvey());

      if (praticaSelf?.value?.compileType !== 'WebCollaboration') {
        navigate(appRoutes.Dashboard, { replace: true });
      }
    }
  }, [
    closeToast,
    dispatch,
    formatMessage,
    name,
    navigate,
    praticaSelf?.value?.compileType,
    showToast,
    surname,
    updateQuestionnairesStatus,
  ]);

  //error
  const findQuestionText = useCallback(
    (idDomanda: number) => {
      const find = (d: Domanda) => d.idDomanda === idDomanda;
      let found;
      if (template)
        found = template.sezioni
          ?.find((s) =>
            s?.listaDomande?.find(
              (d) => find(d) || d.strutturaInternaDomande?.domande?.find(find)
            )
          )
          ?.listaDomande?.find(
            (d) => find(d) || d.strutturaInternaDomande?.domande?.find(find)
          );
      found = found?.strutturaInternaDomande?.domande?.find(find) || found;
      return found?.testoDomanda || '';
    },
    [template]
  );

  const fieldsErrors = useMemo(() => {
    return typeof errors === 'object' ? Object.keys(errors) : [];
  }, [errors]);

  const useEsgCustomMessage = useMemo(() => {
    const invalidIds = [267, 290, 293, 296, 299];
    return fieldsErrors?.some((f) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const errorType = (errors[f] as any)?.type;
      return (
        invalidIds.includes(Number(f.split('-')[1])) &&
        errorType === 'validation' &&
        data[f]
      );
    });
  }, [fieldsErrors, errors, data]);

  const prevSignature = useRef<string>('');

  useEffect(() => {
    // Filter errors to only include 'validation' type where the field has a value
    const validationErrorFields = fieldsErrors.filter((f) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return errors[f] && data[f];
    });

    let signature = '';
    if (useEsgCustomMessage) {
      signature = 'ESG';
    } else if (validationErrorFields.length > 0) {
      signature =
        'GENERIC:' +
        validationErrorFields.sort((a, b) => a.localeCompare(b)).join(',');
    } else {
      signature = 'NONE';
    }

    if (signature !== prevSignature.current) {
      const timer = setTimeout(() => {
        prevSignature.current = signature;

        if (signature === 'ESG') {
          showToast({
            type: 'error',
            title: formatMessage(commonErrorMessages.errorTitlePopup),
            description: formatMessage(
              homeOnboardingSelfMifidSurveyMessages.mifidPfError
            ),
            close: closeToast,
          });
        } else if (signature.startsWith('GENERIC:')) {
          const validationMessages = validationErrorFields.map(
            (f) => `${findQuestionText(Number(f.split('-')[1]))}`
          );

          const first = validationMessages.shift();
          const others = validationMessages
            .map((msg) => msg.split('-')[0].trim())
            .join('", "')
            .trim();

          showToast({
            type: 'error',
            title: formatMessage(commonErrorMessages.errorTitlePopup),
            description: formatMessage(
              homeOnboardingSelfMifidSurveyMessages.mifidGenericError,
              {
                first: `"${first.split('-')[0].trim()}"`,
                others: `"${others}"`,
              }
            ),
            close: closeToast,
          });
        } else {
          closeToast();
        }
      }, 300);

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [
    closeToast,
    errors,
    fieldsErrors,
    formatMessage,
    showToast,
    useEsgCustomMessage,
    data,
    findQuestionText,
  ]);
  //-------------------
  const processedTemplate = useMemo(() => {
    if (!template || !amlAnswers?.precompilato || !amlAnswers.risposte)
      return template;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const prefilledIds = amlAnswers.risposte.map((r: any) => r.idDomanda);

    const newTemplate = JSON.parse(JSON.stringify(template));

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const markReadOnly = (d: any) => {
      if (prefilledIds.includes(d.idDomanda)) {
        d.isReadOnly = true;
      }
      if (d.strutturaInternaDomande?.domande) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        d.strutturaInternaDomande.domande.forEach((sub: any) =>
          markReadOnly(sub)
        );
      }
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    newTemplate.sezioni?.forEach((s: any) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      s.listaDomande?.forEach((d: any) => markReadOnly(d));
    });

    return newTemplate;
  }, [template, amlAnswers]);

  const orderSections = useCallback((a: Sezione, b: Sezione) => {
    const mifidPf = [
      'ANAGRAFICA',
      'CONOSCENZA_ESPERIENZA_PF',
      'FINANZIARIA_PF',
      'TOLLERANZA_OSCILLAZIONE',
      'INVESTIMENTO_PF',
      'INVESTIMENTI_ESG_PF',
      'EDUCAZIONE_FINANZIARIA',
    ];

    return (
      mifidPf.indexOf(a.idSezione || '') - mifidPf.indexOf(b.idSezione || '')
    );
  }, []);

  return {
    template: processedTemplate,
    answers,
    form,
    orderSections,
    errors,
    onSubmit,
    isLoading: saveResponseStatus === 'loading',
  };
};
